import { useEffect, useMemo, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { reportViewMetric } from '../services/api';
import usePrevious from './usePrevious';
import useSelectedEnv from './useSelectedEnv';

type Props = {
  name: string;
  loadDeps: any[];
  contextId?: string;
};

const useViewLoadTracking = ({ name, loadDeps, contextId }: Props) => {
  const previousContextId = usePrevious(contextId);
  const isInitialMount = useRef(true);
  const startTimeRef = useRef<number>(performance.now());
  const envId = useSelectedEnv();
  const envIdRef = useRef(envId);
  envIdRef.current = envId;
  const nameRef = useRef(name);
  nameRef.current = name;

  const isLoaded = useMemo(
    () => loadDeps.every((dep) => dep !== null && dep !== undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...loadDeps],
  );

  useEffect(() => {
    // Only reset if this is NOT the initial mount AND contextId has changed
    if (!isInitialMount.current && previousContextId !== contextId) {
      datadogRum.startView('__reset__');
    }

    // Start the actual view (this closes __reset__ if it was created)
    datadogRum.startView(name);

    startTimeRef.current = performance.now();

    isInitialMount.current = false;

    return () => {
      datadogRum.addTiming('unmount');
    };
  }, [contextId, name, previousContextId]);

  useEffect(() => {
    if (!isLoaded) {
      startTimeRef.current = performance.now();
    } else {
      datadogRum.addTiming('loaded');

      // Report duration as an independent gauge metric via the backend
      if (envIdRef.current) {
        const duration = (performance.now() - startTimeRef.current) / 1000;
        reportViewMetric(
          { view_name: nameRef.current, duration },
          envIdRef.current,
        ).catch(() => {});
      }
    }
  }, [isLoaded]);
};

export default useViewLoadTracking;
